import { inject, provide, type InjectionKey, type MaybeRefOrGetter } from 'vue';
import type { SortDirection } from '@/types/tableTypes';
import type { ScheduleEntry } from './scheduleGroups';

/**
 * The schedules each custom list on the search page draws, keyed by the row's id as a string.
 *
 * The search page works out each row's card schedules once and provides them here. The card and
 * the table's Schedules cell read them from here, so a row is not worked out once per reader. A
 * component outside the search page, or a row missing from the map, works out its own with
 * cardSchedulesFor.
 *
 * The table cell is drawn by ChildrenArrayColumn, which passes its inline component only `row`
 * and `with-title`. That is why the cell injects these instead of taking them as a prop.
 */
export type CardSchedulesByRowId = MaybeRefOrGetter<ReadonlyMap<string, ScheduleEntry[]>>;

const cardSchedulesKey: InjectionKey<CardSchedulesByRowId | null> = Symbol('cardSchedules');

export function provideCardSchedules(cardSchedulesByRowId: CardSchedulesByRowId): void {
    provide(cardSchedulesKey, cardSchedulesByRowId);
}

/** The provided card schedules, or null outside a page that provides them. */
export function injectCardSchedules(): CardSchedulesByRowId | null {
    return inject(cardSchedulesKey, null);
}

/**
 * The sort the search page's rows were searched with, keyed by field in priority order. It is
 * the sort the request sent, which the search returns beside its rows, not the live sort. So
 * while a new sort is loading, the rows on screen keep the line order of the sort that placed
 * them, and a failed search leaves it as it was.
 *
 * The search page provides it around its table only, through TableScheduleSortScope. The table's
 * schedule cells read it through useTableSchedules. The cards and the record pane are outside, so
 * they keep the card order whatever the sort.
 */
export type TableScheduleSort = MaybeRefOrGetter<Readonly<Record<string, SortDirection>> | null | undefined>;

const tableScheduleSortKey: InjectionKey<TableScheduleSort | null> = Symbol('tableScheduleSort');

export function provideTableScheduleSort(searchedSort: TableScheduleSort): void {
    provide(tableScheduleSortKey, searchedSort);
}

/** The provided sort, or null outside a page that provides one. */
export function injectTableScheduleSort(): TableScheduleSort | null {
    return inject(tableScheduleSortKey, null);
}

/**
 * Each row's table schedule lines, keyed like the card schedules: ordered by the searched
 * sort and cut to the lines a cell draws. TableScheduleSortScope works them out once per row,
 * so the "schedules" cell and every schedule column's cell of a row read the same array
 * instead of each sorting the row again.
 */
export type TableSchedulesByRowId = MaybeRefOrGetter<ReadonlyMap<string, ScheduleEntry[]>>;

const tableSchedulesKey: InjectionKey<TableSchedulesByRowId | null> = Symbol('tableSchedules');

export function provideTableSchedules(tableSchedulesByRowId: TableSchedulesByRowId): void {
    provide(tableSchedulesKey, tableSchedulesByRowId);
}

/** The provided table schedule lines, or null outside a table that provides them. */
export function injectTableSchedules(): TableSchedulesByRowId | null {
    return inject(tableSchedulesKey, null);
}
